import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  useLocation,
} from "react-router-dom";

import "./Nav.css";
import "./MobileNav.css";

import logo from "../../images/AxiomosLogo.png";
import servicesData from "../../JsonFiles/Services.json";
import technologyData from "../../JsonFiles/Technologies.json";
import projectData from "../../JsonFiles/Projects.json";
import industriesData from "../../JsonFiles/Industries.json";
import MobileNav from "./MobileNav";

const asText = (value, fallback = "") => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  return fallback;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const scrollToTop = () => {
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  window.scrollTo({
    top: 0,
    left: 0,
    behavior: reduceMotion ? "auto" : "smooth",
  });
};

const Link = ({ to, children, onClick, ...rest }) => {
  const handleClick = (event) => {
    scrollToTop();
    onClick?.(event);
  };

  return (
    <RouterLink to={to} onClick={handleClick} {...rest}>
      {children}
    </RouterLink>
  );
};

const NavLink = ({ to, children, className = "", onClick, ...rest }) => {
  const handleClick = (event) => {
    scrollToTop();
    onClick?.(event);
  };

  return (
    <RouterNavLink
      to={to}
      onClick={handleClick}
      className={({ isActive }) =>
        `${className}${isActive ? " is-active" : ""}`
      }
      {...rest}
    >
      {children}
    </RouterNavLink>
  );
};

const DropdownChevron = () => (
  <svg
    className="nav-chevron"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="11" cy="11" r="6.8" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M16.2 16.2L21 21"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M6 6L18 18M18 6L6 18"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12H19M14 7L19 12L14 17"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const makeMenuItems = (data, route) =>
  asArray(data)
    .map((item, index) => {
      const id = asText(item?.id ?? item?.slug ?? index + 1);
      const title = asText(item?.title ?? item?.name);

      if (!title) return null;

      return {
        id: `${route}-${id}`,
        title,
        description: asText(
          item?.descriptionsmall ??
            item?.shortDescription ??
            item?.description,
        ),
        to: `${route}/${encodeURIComponent(id)}`,
      };
    })
    .filter(Boolean);

const SERVICE_ITEMS = makeMenuItems(servicesData, "/services");
const TECHNOLOGY_ITEMS = makeMenuItems(technologyData, "/technologies");
const INDUSTRY_ITEMS = makeMenuItems(industriesData, "/industries");

const COMPANY_ITEMS = [
  {
    id: "company-about",
    title: "About Axiomos",
    description: "Our purpose, approach, and engineering principles.",
    to: "/about",
  },
  {
    id: "company-careers",
    title: "Careers",
    description: "Build meaningful technology with our growing team.",
    to: "/career",
  },
  {
    id: "company-contact",
    title: "Contact",
    description: "Start a conversation about your next digital product.",
    to: "/contact",
  },
];

const SEARCH_ITEMS = [
  ...SERVICE_ITEMS.map((item) => ({ ...item, type: "Service" })),
  ...TECHNOLOGY_ITEMS.map((item) => ({ ...item, type: "Technology" })),
  ...INDUSTRY_ITEMS.map((item) => ({ ...item, type: "Industry" })),
  ...makeMenuItems(projectData, "/projects").map((item) => ({
    ...item,
    type: "Case study",
  })),
  ...COMPANY_ITEMS.map((item) => ({ ...item, type: "Company" })),
  {
    id: "search-blog",
    title: "Ideas & Insights",
    description: "Perspectives on software, products, and digital transformation.",
    to: "/blog",
    type: "Blog",
  },
];

const Dropdown = ({
  label,
  to,
  description,
  items,
  isOpen,
  onOpen,
  onClose,
  onToggle,
}) => {
  const menuId = `desktop-nav-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <li
      className={`nav-item nav-dropdown${isOpen ? " is-open" : ""}`}
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onFocusCapture={onOpen}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) onClose();
      }}
    >
      <div className="nav-dropdown-control">
        <NavLink to={to} className="nav-link" onClick={onClose}>
          {label}
        </NavLink>
        <button
          type="button"
          className="nav-dropdown-toggle"
          onClick={onToggle}
          aria-label={`${isOpen ? "Close" : "Open"} ${label} menu`}
          aria-expanded={isOpen}
          aria-controls={menuId}
        >
          <DropdownChevron />
        </button>
      </div>

      {isOpen && (
        <div id={menuId} className="nav-mega-menu" aria-label={`${label} menu`}>
          <div className="nav-mega-menu__intro">
            <span>Explore {label}</span>
            <h2>{label}</h2>
            <p>{description}</p>
            <Link to={to} className="nav-mega-menu__overview" onClick={onClose}>
              View all {label.toLowerCase()}
              <ArrowIcon />
            </Link>
          </div>

          <div className="nav-mega-menu__links">
            {items.map((item) => (
              <Link
                key={item.id}
                to={item.to}
                className="nav-mega-menu__item"
                onClick={onClose}
              >
                <span>{item.title}</span>
                {item.description && <small>{item.description}</small>}
                <ArrowIcon />
              </Link>
            ))}
          </div>
        </div>
      )}
    </li>
  );
};

const SearchPanel = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const panelRef = useRef(null);

  const results = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    if (!normalizedQuery) return SEARCH_ITEMS.slice(0, 7);

    return SEARCH_ITEMS.filter((item) =>
      [item.title, item.description, item.type]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery),
    ).slice(0, 10);
  }, [query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => inputRef.current?.focus(), 30);

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="nav-search-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="nav-search-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div ref={panelRef} className="nav-search-panel__dialog">
        <div className="nav-search-panel__topbar">
          <div>
            <span>AX / Search</span>
            <strong id="nav-search-title">Find what you need</strong>
          </div>
          <button type="button" onClick={onClose} aria-label="Close search">
            <CloseIcon />
          </button>
        </div>

        <label className="nav-search-panel__field">
          <span className="nav-sr-only">Search Axiomos website</span>
          <SearchIcon />
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search services, technologies, industries, or projects"
            autoComplete="off"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")}>
              Clear
            </button>
          )}
        </label>

        <div className="nav-search-panel__result-header">
          <span>{query ? "Search results" : "Suggested pages"}</span>
          <small>{results.length} available</small>
        </div>

        {results.length > 0 ? (
          <div className="nav-search-panel__results">
            {results.map((item) => (
              <Link
                key={item.id}
                to={item.to}
                className="nav-search-panel__result"
                onClick={onClose}
              >
                <span>{item.type}</span>
                <div>
                  <strong>{item.title}</strong>
                  {item.description && <p>{item.description}</p>}
                </div>
                <ArrowIcon />
              </Link>
            ))}
          </div>
        ) : (
          <div className="nav-search-panel__empty" role="status">
            <strong>No matching pages found</strong>
            <p>Try a broader keyword or contact our team for guidance.</p>
            <Link to="/contact" onClick={onClose}>
              Contact Axiomos
              <ArrowIcon />
            </Link>
          </div>
        )}

        <p className="nav-search-panel__hint">
          Press <kbd>Esc</kbd> to close
        </p>
      </div>
    </div>
  );
};

const NavBar = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const headerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setOpenMenu(null);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 18);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (openMenu && !headerRef.current?.contains(event.target)) {
        setOpenMenu(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpenMenu(null);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenu]);

  const dropdownProps = (name) => ({
    isOpen: openMenu === name,
    onOpen: () => setOpenMenu(name),
    onClose: () => setOpenMenu(null),
    onToggle: () => setOpenMenu((current) => (current === name ? null : name)),
  });

  return (
    <>
      <header
        ref={headerRef}
        className={`desktop-header${isScrolled ? " is-scrolled" : ""}`}
      >
        <nav className="navbar" aria-label="Main navigation">
          <div className="navbar-container">
            <Link to="/" className="navbar-brand" aria-label="Axiomos home">
              <img
                src={logo}
                alt="Axiomos Technologies"
                className="logo-nav"
                decoding="async"
              />
            </Link>

            <div className="nav-primary">
              <ul className="navbar-nav">
                <li className="nav-item">
                  <NavLink to="/projects" className="nav-link">
                    Case Studies
                  </NavLink>
                </li>

                <Dropdown
                  label="Services"
                  to="/services"
                  description="End-to-end digital product services shaped around your business goals."
                  items={SERVICE_ITEMS}
                  {...dropdownProps("services")}
                />

                <Dropdown
                  label="Technologies"
                  to="/technologies"
                  description="Modern platforms and engineering tools selected for dependable outcomes."
                  items={TECHNOLOGY_ITEMS.slice(0, 10)}
                  {...dropdownProps("technologies")}
                />

                <Dropdown
                  label="Industries"
                  to="/industries"
                  description="Focused digital solutions grounded in real operational challenges."
                  items={INDUSTRY_ITEMS}
                  {...dropdownProps("industries")}
                />

                <Dropdown
                  label="Company"
                  to="/about"
                  description="Meet the people, principles, and purpose behind Axiomos."
                  items={COMPANY_ITEMS}
                  {...dropdownProps("company")}
                />

                <li className="nav-item">
                  <NavLink to="/blog" className="nav-link">
                    Blog
                  </NavLink>
                </li>
              </ul>
            </div>

            <div className="nav-actions">
              <button
                type="button"
                className="nav-search"
                onClick={() => {
                  setOpenMenu(null);
                  setSearchOpen(true);
                }}
                aria-label="Search Axiomos website"
                aria-expanded={searchOpen}
              >
                <SearchIcon />
                <span>Search</span>
              </button>

              <Link to="/contact" className="contact-button">
                Contact us
                <ArrowIcon />
              </Link>
            </div>
          </div>
        </nav>
      </header>

      <SearchPanel isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

const getIsMobile = () => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 1079px)").matches;
};

const MainNavBar = () => {
  const [isMobile, setIsMobile] = useState(getIsMobile);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1079px)");
    const handleScreenChange = (event) => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleScreenChange);
    } else {
      mediaQuery.addListener(handleScreenChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleScreenChange);
      } else {
        mediaQuery.removeListener(handleScreenChange);
      }
    };
  }, []);

  return isMobile ? <MobileNav /> : <NavBar />;
};

export default MainNavBar;
